import React from 'react';
import {
  ChevronLeft,
  MoreHorizontal,
  BadgeCheck,
  Grid3x3,
  Clapperboard,
  Bookmark,
  UserSquare2,
  Search,
  Wifi,
  BatteryFull,
  Signal,
} from 'lucide-react';

/**
 * InstagramPreview — a phone-shaped mockup that renders a real Instagram
 * profile UI for @bkkbutter using her own photos. We render this instead of
 * an <iframe> because Instagram blocks cross-origin framing via
 * X-Frame-Options. This always renders and stays bespoke.
 */
export default function InstagramPreview({ ig, time = '9:41' }) {
  return (
    <div className="relative w-full h-full flex items-center justify-center">
      {/* Phone frame */}
      <div
        className="relative rounded-[38px] p-[6px] shrink-0"
        style={{
          width: '100%',
          maxWidth: 280,
          aspectRatio: '9 / 19',
          background:
            'linear-gradient(160deg, #2a2e2b 0%, #1a1d1b 55%, #0f1110 100%)',
          boxShadow:
            '0 30px 60px -30px rgba(15,27,20,0.45), 0 1px 0 rgba(255,255,255,0.05) inset',
        }}
      >
        {/* Screen */}
        <div
          className="relative w-full h-full rounded-[32px] overflow-hidden flex flex-col"
          style={{ background: '#ffffff' }}
        >
          {/* Status bar */}
          <div
            className="flex items-center justify-between px-5 pt-2 pb-1 shrink-0"
            style={{ fontSize: 10, color: '#0b0d0c' }}
          >
            <span className="mono" style={{ fontWeight: 600 }}>
              {time}
            </span>
            <div className="flex items-center gap-1">
              <Signal size={10} strokeWidth={2.25} />
              <Wifi size={10} strokeWidth={2.25} />
              <BatteryFull size={12} strokeWidth={2} />
            </div>
          </div>

          {/* App header (username + icons) */}
          <div
            className="flex items-center justify-between px-3 py-2 border-b shrink-0"
            style={{ borderColor: 'rgba(0,0,0,0.08)' }}
          >
            <ChevronLeft size={16} strokeWidth={2} color="#0b0d0c" />
            <div className="flex items-center gap-1">
              <span
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: '#0b0d0c',
                  letterSpacing: '-0.01em',
                }}
              >
                {ig.handle}
              </span>
              <BadgeCheck size={12} strokeWidth={2.25} color="#3B82F6" />
            </div>
            <MoreHorizontal size={16} strokeWidth={2} color="#0b0d0c" />
          </div>

          {/* Profile section */}
          <div className="px-3 py-3 shrink-0">
            <div className="flex items-center gap-3">
              {/* Avatar with gradient ring */}
              <div
                className="rounded-full p-[2px] shrink-0"
                style={{
                  background:
                    'linear-gradient(135deg, #F58529, #DD2A7B, #8134AF)',
                }}
              >
                <div
                  className="rounded-full p-[2px]"
                  style={{ background: '#ffffff' }}
                >
                  <div
                    className="w-[54px] h-[54px] rounded-full bg-center bg-cover"
                    style={{ backgroundImage: `url(${ig.avatar})` }}
                  />
                </div>
              </div>

              {/* Stats */}
              <div className="flex-1 flex items-center justify-around">
                <Stat value={ig.posts} label="posts" />
                <Stat value={ig.followers} label="followers" />
                <Stat value={ig.following} label="following" />
              </div>
            </div>

            {/* Name + bio */}
            <div className="mt-2">
              <div
                style={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: '#0b0d0c',
                  lineHeight: 1.2,
                }}
              >
                {ig.displayName}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: '#0b0d0c',
                  lineHeight: 1.4,
                  marginTop: 2,
                  whiteSpace: 'pre-line',
                }}
              >
                {ig.bio}
              </div>
            </div>

            {/* Action buttons */}
            <div className="mt-2.5 flex items-center gap-1.5">
              <button
                className="flex-1 rounded-md"
                style={{
                  background: '#0095F6',
                  color: '#ffffff',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 0',
                }}
              >
                Follow
              </button>
              <button
                className="flex-1 rounded-md border"
                style={{
                  background: '#ffffff',
                  borderColor: 'rgba(0,0,0,0.12)',
                  color: '#0b0d0c',
                  fontSize: 11,
                  fontWeight: 600,
                  padding: '5px 0',
                }}
              >
                Message
              </button>
              <button
                className="rounded-md border"
                style={{
                  background: '#ffffff',
                  borderColor: 'rgba(0,0,0,0.12)',
                  padding: '5px 8px',
                }}
              >
                <Search size={10} strokeWidth={2.5} color="#0b0d0c" />
              </button>
            </div>
          </div>

          {/* Tab bar */}
          <div
            className="flex items-center justify-around border-y shrink-0"
            style={{ borderColor: 'rgba(0,0,0,0.08)', padding: '6px 0' }}
          >
            <TabIcon active>
              <Grid3x3 size={14} strokeWidth={2} />
            </TabIcon>
            <TabIcon>
              <Clapperboard size={14} strokeWidth={2} />
            </TabIcon>
            <TabIcon>
              <UserSquare2 size={14} strokeWidth={2} />
            </TabIcon>
            <TabIcon>
              <Bookmark size={14} strokeWidth={2} />
            </TabIcon>
          </div>

          {/* Photo grid */}
          <div
            className="grid grid-cols-3 flex-1 min-h-0"
            style={{ gap: 2, background: 'rgba(0,0,0,0.08)' }}
          >
            {ig.grid.slice(0, 6).map((src, i) => (
              <div
                key={i}
                className="bg-center bg-cover"
                style={{ backgroundImage: `url(${src})` }}
              />
            ))}
          </div>

          {/* Home indicator */}
          <div className="flex justify-center shrink-0 pb-1 pt-1.5">
            <div
              className="rounded-full"
              style={{ width: 80, height: 3, background: 'rgba(0,0,0,0.45)' }}
            />
          </div>
        </div>

        {/* Dynamic island / camera pill */}
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full"
          style={{
            top: 10,
            width: 70,
            height: 16,
            background: '#0b0d0c',
          }}
          aria-hidden
        />
      </div>

      {/* LIVE indicator */}
      <div
        className="absolute top-3 left-3 inline-flex items-center gap-1.5 mono text-[9px] uppercase tracking-[0.18em] px-2 py-1 rounded-full"
        style={{
          background: 'var(--cream)',
          color: 'var(--forest)',
          border: '1px solid rgba(15,27,20,0.1)',
        }}
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{
            background: '#e11d48',
            boxShadow: '0 0 0 3px rgba(225,29,72,0.18)',
          }}
        />
        Live feed
      </div>
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div className="text-center">
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0b0d0c', lineHeight: 1 }}>
        {value}
      </div>
      <div
        style={{
          fontSize: 10,
          color: '#0b0d0c',
          marginTop: 2,
        }}
      >
        {label}
      </div>
    </div>
  );
}

function TabIcon({ children, active }) {
  return (
    <div
      style={{
        color: active ? '#0b0d0c' : 'rgba(0,0,0,0.4)',
        borderTop: active ? '1.5px solid #0b0d0c' : '1.5px solid transparent',
        marginTop: -6,
        paddingTop: 4,
      }}
    >
      {children}
    </div>
  );
}
